import { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import messages from '../../constants/messages';
import { PLANS_STATUS, TRIAL_PLAN } from '../../constants/subscription';

import { activateTrial, getMyPlan, getUserSubscriptionById } from '../../redux/actions/subscription';
import { subscribeUser, updateSubscription } from '../../redux/actions/user';

import { errorToastHandler, successToastHandler } from '../../components/action_notifier';

import './styles.scss';

import ConfirmationModal from '../Modals/ConfirmationModal';
import SubmitSubscriptionModal from '../Modals/SubmitSubscriptionModal';
import ButtonRow from './ButtonRow';
import FeatureRow from './FeatureRow';
import PlanRow from './PlanRow';

const SubscriptionTable = ({ closePlanModal }) => {
  const dispatch = useDispatch();
  const { featureOptions, myPlan } = useSelector(state => state.subscription);

  const [selectedPlan, setSelectedPlan] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);
  const [isOpenSubmitSubscriptionModal, setOpenSubmitSubscriptionModal] = useState(false);
  const [isOpenConfirmationModal, setOpenConfirmationModal] = useState(false);

  const activateTrialPeriod = useCallback(async planId => {
    try {
      await dispatch(activateTrial({ planId }));
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_CREATE_DATA('Trial Subscription'));
    }
  }, []);

  const onSelectPlan = useCallback(
    async (plan, isUpdate) => {
      let actualPlan = plan;
      const isUserEligibleForTrial = false;

      if (isUserEligibleForTrial) {
        await activateTrialPeriod(plan?.id);
        successToastHandler('Trial subscription activated!');
        closePlanModal();
        await dispatch(getMyPlan());

        return;
      }

      const isSelectedPlanSameToCurrent = plan.id === myPlan?.id;

      if (isSelectedPlanSameToCurrent) {
        if (myPlan?.status === PLANS_STATUS.ACTIVE || myPlan?.status === PLANS_STATUS.INACTIVE) {
          actualPlan = myPlan;
        }

        if (myPlan?.status === PLANS_STATUS.PENDING) {
          const data = await dispatch(getUserSubscriptionById(myPlan?.user_plan_id));
          actualPlan = data?.plan;
        }
      }

      setSelectedPlan(actualPlan);
      setIsUpdate(isUpdate);

      if (plan.type === TRIAL_PLAN) {
        setOpenConfirmationModal(true);
        return;
      }

      setOpenSubmitSubscriptionModal(true);
    },
    [myPlan]
  );

  const onCancelSubmitSubscriptionModal = useCallback(() => {
    setOpenSubmitSubscriptionModal(false);
  }, []);

  const openLink = link => {
    if (!link) return;

    const newWindow = window.open(link, '_blank', 'noopener,noreferrer');

    if (newWindow) {
      newWindow.opener = null;
    }
  };

  const onSubmitSubscriptionModal = useCallback(
    async (period = 1, limits, isUpdate) => {
      const { feature_options } = selectedPlan;
      let payload = {};

      if (selectedPlan.type === TRIAL_PLAN) {
        payload = {
          period: 1,
          limits: [],
          plan_id: selectedPlan.id
        };
      } else {
        const limitOptions = Object.keys(limits).map(limitOption => {
          const featureOption = feature_options.find(item => {
            return item.type === limitOption;
          });
          return {
            feature_option_id: featureOption.id,
            count: limits[limitOption]
          };
        });
        payload = {
          period,
          limits: limitOptions,
          plan_id: selectedPlan.id
        };
      }

      if (isUpdate) {
        const response = await dispatch(updateSubscription(payload));

        openLink(response?.url);
      } else {
        const response = await dispatch(subscribeUser(payload));

        openLink(response?.url);
      }

      setOpenSubmitSubscriptionModal(false);
      setOpenConfirmationModal(false);
      setIsUpdate(false);
      await dispatch(getMyPlan());
    },
    [selectedPlan]
  );
  return (
    <div className="subscription-table">
      <PlanRow />

      {featureOptions?.map((feature, index) => (
        <FeatureRow feature={feature} key={feature.title + index} />
      ))}

      <ButtonRow onSelect={onSelectPlan} />

      <SubmitSubscriptionModal
        isOpen={isOpenSubmitSubscriptionModal}
        isUpdate={isUpdate}
        plan={selectedPlan}
        onCancel={onCancelSubmitSubscriptionModal}
        onSubmit={onSubmitSubscriptionModal}
      />

      <ConfirmationModal
        description="Are you sure you want to request trial plan for 1 month?"
        submitButtonType="primary"
        submitText="Request"
        title="Trial plan"
        isOpen={isOpenConfirmationModal}
        onCancel={() => setOpenConfirmationModal(false)}
        onSubmit={onSubmitSubscriptionModal}
      />
    </div>
  );
};

export default memo(SubscriptionTable);
